import React,{useEffect,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const {
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiTrash2
}=FiIcons;

export default function RawLogsTab() {
  const [logs,setLogs]=useState([]);
  const [expandedId,setExpandedId]=useState(null);
  const [loading,setLoading]=useState(true);
  const [deletingId,setDeletingId]=useState(null);
  const [isClearing,setIsClearing]=useState(false);

  const fetchLogs=async ()=> {
    setLoading(true);
    const {data}=await supabase
      .from('raw_order_logs_1787306056654')
      .select('*')
      .order('created_at',{ascending: false})
      .limit(100);

    setLogs(data || []);
    setLoading(false);
  };

  useEffect(()=> {
    fetchLogs();
  },[]);

  const deleteLog=async (id)=> {
    if (!window.confirm('Delete this raw webhook log permanently?')) return;

    setDeletingId(id);
    const {error}=await supabase
      .from('raw_order_logs_1787306056654')
      .delete()
      .eq('id',id);

    if (error) {
      alert(`Failed to delete raw log: ${error.message}`);
    } else {
      setExpandedId((current)=> current===id ? null : current);
      await fetchLogs();
    }

    setDeletingId(null);
  };

  const clearLogs=async ()=> {
    if (!window.confirm('Delete every raw webhook log permanently? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);
    const {error}=await supabase
      .from('raw_order_logs_1787306056654')
      .delete()
      .not('id','is',null);

    if (error) {
      alert(`Failed to clear raw logs: ${error.message}`);
    } else {
      setExpandedId(null);
      await fetchLogs();
    }

    setIsClearing(false);
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Raw Order Webhook Logs
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Original order-created payloads received from LegUpRX.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="text-teal-600 hover:text-teal-700 bg-teal-50 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition disabled:opacity-60"
          >
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={clearLogs}
            disabled={isClearing || logs.length===0}
            className="text-red-600 hover:text-red-700 bg-red-50 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition disabled:opacity-60"
          >
            <SafeIcon icon={FiTrash2} />
            {isClearing ? 'Deleting...' : 'Delete All'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-slate-100 text-slate-700 border-b border-slate-200 uppercase text-xs font-bold tracking-wider">
            <tr>
              <th className="p-4">Received</th>
              <th className="p-4">Order ID</th>
              <th className="p-4">Transaction ID</th>
              <th className="p-4">Slot</th>
              <th className="p-4 text-right">Payload</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-slate-500">
                  Loading webhook logs...
                </td>
              </tr>
            ) : logs.length===0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-slate-500">
                  No order-created webhooks have been received yet.
                </td>
              </tr>
            ) : logs.map((log)=> (
              <React.Fragment key={log.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="p-4 text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="p-4 font-medium text-slate-700">
                    {log.order_id || '-'}
                  </td>
                  <td className="p-4 font-mono text-xs text-slate-600">
                    {log.transaction_id || '-'}
                  </td>
                  <td className="p-4 font-bold text-slate-700">
                    {log.slot_number ? `#${log.slot_number}` : '-'}
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={()=> setExpandedId(expandedId===log.id ? null : log.id)}
                        className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium"
                      >
                        {expandedId===log.id ? 'Hide' : 'View'}
                        <SafeIcon icon={expandedId===log.id ? FiChevronUp : FiChevronDown} />
                      </button>
                      <button
                        onClick={()=> deleteLog(log.id)}
                        disabled={deletingId===log.id}
                        aria-label="Delete raw webhook log"
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId===log.id && (
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <td colSpan="5" className="p-4">
                      <pre className="max-h-96 overflow-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-teal-200">
                        {JSON.stringify(log.payload,null,2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}